import { Link } from 'react-router-dom';
import { ScrollText } from 'lucide-react';

import type { Migration, MigrationDevice, WaveDefaults } from '../api/migrations';
import { AbLink, Fact, FactList } from './abPrimitives';
import { ContextCard } from './ContextColumn';
import GroupLabel from './GroupLabel';
import TenantLabel from './TenantLabel';
import WavePanel from './WavePanel';
import { ACCENT } from '../theme/tokens';

// The campaign detail page's right-hand context column (FD-491), closing the
// gap FD-ADR-043's own close-out records: `ContextColumn` shipped as a tested
// primitive with ZERO callers, and the campaign-detail port kept `WavePanel` in
// the Overview main column instead.
//
// WHY THIS IS NOT THE SECOND RAIL ADR-031 FORBADE, since that rule is one
// careless PR away from being re-broken here. ADR-031 refused a second
// FULL-HEIGHT NAVIGATION column — three of them had grown (MigrationSubRail,
// TenantSubRail, GroupSubRail) and each one navigated the app. This column
// holds the facts and the actions FOR THE RECORD ON SCREEN: the wave credential
// and its lifecycle buttons, and the handful of records this campaign points at.
// It does not carry the console's navigation, it does not persist across routes,
// and it stacks below the content under 1280px rather than surviving as a strip.
//
// A cross-link to a related RECORD is a fact about this record, not a
// navigation structure — which is why "Related resources" is links and is still
// not a rail. The line to hold is that nothing here navigates the CONSOLE: no
// section list, no route tree, no tabs. If a future change wants those, it wants
// the rail, and the rail already exists.

/**
 * The DOM id the wave panel carries, so a surface in the Outlet can scroll to a
 * panel that now lives in the shell around it.
 *
 * `MigrationOverviewPage`'s delete dialog offers "Go to the wave panel" when a
 * delete is refused with `409 WAVE_ACTIVE` (FD-ADR-044 D1). It used to hold a
 * React ref to a panel in its own tree; the panel moved out, and a ref cannot
 * cross a route boundary. An id can, and the constant lives here — beside the
 * element it names — so the two cannot drift into a silently dead scroll.
 */
export const CAMPAIGN_WAVE_ANCHOR_ID = 'campaign-wave';

export interface CampaignContextColumnProps {
  migration: Migration;
  devices: MigrationDevice[];
  waveDefaults?: WaveDefaults;
}

export default function CampaignContextColumn({
  migration,
  devices,
  waveDefaults,
}: CampaignContextColumnProps) {
  const isCloud = migration.migrationType === 'T2T';

  return (
    <>
      {/* `WavePanel` is MOVED, not rewritten — it and the panels it composes
          (`WavePackagePanel`, `WaveCanaryPanel`, `WaveFailedDevicesPanel`, and
          the activate/complete/abort dialogs) mint, revoke and abort a real
          tenant-join credential. A second implementation would drift from the
          first exactly where correctness lives. It brings its own bordered
          section and its own "Wave" heading, so it is NOT wrapped in a
          ContextCard: two borders and two titles around one panel. */}
      <div id={CAMPAIGN_WAVE_ANCHOR_ID}>
        <WavePanel
          migration={migration}
          // FD-358 — devices held at the FD-ADR-036 identity gate. Derived from
          // the devices the layout already loaded, so the panel stays
          // presentational and no second request is made.
          identityHeldCount={devices.filter((d) => d.identityStatus === 'not-ready').length}
          // FD-442 — the deployment's default wave-BPRT lifetime, so the
          // activate dialog pre-fills the number the SERVER will apply.
          // Undefined against a Manager older than FD-442; the dialog falls back
          // to 30.
          defaultExpiryDays={waveDefaults?.bprtExpiryDays}
        />
      </div>

      <ContextCard title="Related resources">
        <FactList>
          {/* Only Cloud has a source TENANT. For Hybrid the origin is an
              on-premises domain, which is not a record this console holds — and
              `Resolve-MigrationTenant` answers `400 D2C_HAS_NO_SOURCE_TENANT`
              for the same reason. An empty row would read as missing data. */}
          {isCloud && (
            <Fact label="Source tenant">
              {migration.sourceTenantId ? (
                <AbLink to={`/tenants/${encodeURIComponent(migration.sourceTenantId)}/overview`}>
                  <TenantLabel
                    domain={migration.sourceTenantDomain}
                    tenantId={migration.sourceTenantId}
                  />
                </AbLink>
              ) : (
                <TenantLabel domain={null} tenantId={null} />
              )}
            </Fact>
          )}

          <Fact label="Destination">
            {migration.destTenantId ? (
              <AbLink to={`/tenants/${encodeURIComponent(migration.destTenantId)}/overview`}>
                <TenantLabel
                  domain={migration.destTenantDomain}
                  tenantId={migration.destTenantId}
                />
              </AbLink>
            ) : (
              // FD-ADR-029 §1 — a Hybrid campaign with no explicit destination
              // lands in the DEPLOYMENT's own tenant. That is a real answer and
              // is named as one; an em-dash would read as a value nobody set.
              <TenantLabel
                domain={null}
                tenantId={null}
                fallback={isCloud ? '—' : 'Deployment tenant'}
              />
            )}
          </Fact>

          <Fact label="Group">
            {migration.migrationGroupId && migration.migrationGroupName ? (
              <AbLink to={`/groups/${encodeURIComponent(migration.migrationGroupId)}/overview`}>
                {migration.migrationGroupName}
              </AbLink>
            ) : (
              // Ungrouped, and a DANGLING group id, are different states and
              // GroupLabel is the one place that keeps them apart (FD-ADR-030).
              // Neither is a link: there is nothing to open.
              <GroupLabel
                migrationGroupId={migration.migrationGroupId}
                migrationGroupName={migration.migrationGroupName}
              />
            )}
          </Fact>
        </FactList>

        <div className="mt-3">
          <Link
            to={`/migrations/${encodeURIComponent(migration.migrationId)}/events`}
            className="inline-flex items-center gap-1.5 text-[12px] no-underline hover:underline"
            style={{ color: ACCENT.pressed }}
          >
            <ScrollText size={14} strokeWidth={1.5} />
            Event log
          </Link>
        </div>
      </ContextCard>
    </>
  );
}
